import { useFetchThreeDCategoryAutocomplete, useFetchThreeDCategoryList, useFetchThreeDImageList } from '@api';
import { IThreeDCategoryListItem, IThreeDImageListItem } from '@types';
import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useThreeD = (scrollToTop?: () => void) => {
  const [inputKeyword, setInputKeyword] = useState<string>('');
  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);

  const [initialCategoryList, setInitialCategoryList] = useState<IThreeDCategoryListItem[]>([]); // GET /assets-3d/categories 응답 데이터
  const [searchedCategoryList, setSearchedCategoryList] = useState<IThreeDCategoryListItem[]>([]); // 카테고리 검색 결과
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(new Set());
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedAssetThreeDId, setSelectedAssetThreeDId] = useState<number | null>(null);

  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;

  useFetchThreeDCategoryList({
    onSuccess: (data) => {
      const categoryListData = data.data.data;
      setInitialCategoryList([...categoryListData]);
    }
  });

  const { data: categoryAutocompleteData } = useFetchThreeDCategoryAutocomplete(
    autocompleteKeyword && autocompleteKeyword.length > 0 ? autocompleteKeyword : null
  );
  const autocompleteList =
    autocompleteKeyword &&
    autocompleteKeyword.length > 0 &&
    categoryAutocompleteData &&
    categoryAutocompleteData.length > 0
      ? categoryAutocompleteData.map((item) => item.nameKo)
      : [];

  const createQueryStringForThreeDImageList = () => {
    const temp = [];
    const take = 21;

    temp.push(`&take=${take}`);

    if (selectedCategoryId) {
      temp.push(`&categoryId=${selectedCategoryId}`);
    }

    const qs = temp.join('');

    return qs;
  };

  const {
    data: imageListData,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isFetching
  } = useFetchThreeDImageList(createQueryStringForThreeDImageList(), {
    onSuccess: (data, variables) => {
      // pageParam이 1인 경우에만 스크롤업 실행
      if (variables.pageParam === 1) {
        if (scrollToTop) {
          scrollToTop();
        } else {
          // 기본 동작 (다른 페이지들용)
          window.scrollTo({
            top: 0
          });
        }
      }
    }
  });

  const flattenImageList = useMemo(() => {
    const dataArrLength = imageListData?.pages.length;
    const flattenedArr: IThreeDImageListItem[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (imageListData.pages[i].data.data.length > 0) {
          imageListData.pages[i].data.data.map((item: IThreeDImageListItem) => {
            flattenedArr.push(item);
          });
        }
        i++;
      }

      return flattenedArr;
    } else {
      return [];
    }
  }, [imageListData]);

  // 엔터 입력 시 검색 진행, handleInputKeywordChange보다 먼저 실행됨
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'Enter': {
        e.preventDefault();
        e.stopPropagation();

        handleSubmit();

        break;
      }
      default: {
        return;
      }
    }
  };

  // 카테고리명 인풋 입력시
  const handleInputKeywordChange = (event: React.SyntheticEvent, newValue: string) => {
    // 일반 문자 입력 시: 상태 업데이트
    if (newValue.trim().length > 0) {
      setInputKeyword(newValue);
      handleAutocompleteKeywordChange(newValue);
    } else {
      setInputKeyword('');
      handleAutocompleteKeywordChange('');
      setSearchKeyword(null);
    }
  };

  // 오토컴플리트 리스트 불러오기에 이용할 키워드 setState
  const handleAutocompleteKeywordChange = useCallback(
    debounce((newValue: string) => {
      setAutocompleteKeyword(newValue);
    }, 500),
    []
  );

  // 오토컴플리트 리스트에서 키워드 선택 시 추가
  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue && typeof newValue === 'string' && newValue.trim().length > 0) {
      setInputKeyword(newValue);
      setAutocompleteKeyword('');
      setSearchKeyword(newValue); // 검색 요청

      // debounce된 함수 호출 취소하여 setAutocompleteKeyword(value) 재실행 방지
      handleAutocompleteKeywordChange.cancel();
    }
  };

  const handleClear = () => {
    setInputKeyword('');
    setAutocompleteKeyword('');
    setSearchKeyword(null);
  };

  // 검색 요청 - 키워드 직접 입력 후 엔터시
  const handleSubmit = () => {
    setAutocompleteKeyword('');
    setSearchKeyword(inputKeyword);
  };

  const handleCategoryExpand = (id: number) => {
    setExpandedCategoryIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleCategoryClick = (id: number) => {
    if (id === selectedCategoryId) {
      setSelectedCategoryId(null);
    } else {
      setSelectedCategoryId(id);
    }
  };

  // selectedCategoryId로부터 루트까지의 경로를 nameKo 배열로 반환
  const getCategoryPathById = useCallback(
    (categoryId: number | null, categories: IThreeDCategoryListItem[]): string[] => {
      if (!categoryId) {
        return [];
      }

      // 재귀적으로 카테고리를 탐색하여 경로 찾기
      const findPath = (
        categoryList: IThreeDCategoryListItem[],
        targetId: number,
        currentPath: string[] = []
      ): string[] | null => {
        for (const category of categoryList) {
          const newPath = [...currentPath, category.nameKo];

          // 현재 카테고리가 타겟인 경우
          if (category.id === targetId) {
            return newPath;
          }

          // 자식 카테고리에서 재귀적으로 탐색
          if (category.children && category.children.length > 0) {
            const result = findPath(category.children, targetId, newPath);
            if (result) {
              return result;
            }
          }
        }
        return null;
      };

      const path = findPath(categories, categoryId);
      return path || [];
    },
    []
  );

  // selectedCategoryId에 대한 경로를 계산하는 useMemo
  const selectedCategoryPath = useMemo(() => {
    const categoryList = searchKeyword && searchKeyword.trim().length > 0 ? searchedCategoryList : initialCategoryList;
    return getCategoryPathById(selectedCategoryId, categoryList);
  }, [selectedCategoryId, searchKeyword, searchedCategoryList, initialCategoryList, getCategoryPathById]);

  // 이미지 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  // searchKeyword가 변경될 때 검색된 카테고리 리스트 업데이트
  useEffect(() => {
    if (searchKeyword && searchKeyword.trim().length > 0 && initialCategoryList.length > 0) {
      const trimmedKeyword = searchKeyword.trim();
      const matchedNodes: Array<{
        node: IThreeDCategoryListItem;
        root: IThreeDCategoryListItem;
        parentPath: IThreeDCategoryListItem[];
      }> = [];

      // 검색 키워드가 nameKo에 포함된 모든 노드를 찾고 부모 경로를 추적
      const findMatchingNodes = (
        categories: IThreeDCategoryListItem[],
        path: IThreeDCategoryListItem[],
        root: IThreeDCategoryListItem
      ) => {
        for (const category of categories) {
          // 부분 일치 검색
          if (category.nameKo.includes(trimmedKeyword)) {
            matchedNodes.push({
              node: category,
              root: root,
              parentPath: [...path]
            });
          }

          if (category.children && category.children.length > 0) {
            const newPath = [...path, category];
            findMatchingNodes(category.children, newPath, root);
          }
        }
      };

      // 각 루트에 대해 매칭 노드 찾기
      for (const root of initialCategoryList) {
        findMatchingNodes([root], [], root);
      }

      if (matchedNodes.length > 0) {
        // 루트별로 매칭 노드 그룹화
        const matchedNodesByRoot = new Map<number, typeof matchedNodes>();
        for (const matchedNode of matchedNodes) {
          const rootId = matchedNode.root.id;
          if (!matchedNodesByRoot.has(rootId)) {
            matchedNodesByRoot.set(rootId, []);
          }
          matchedNodesByRoot.get(rootId)!.push(matchedNode);
        }

        // 각 루트에 대해 필터링된 트리 생성
        const filteredTrees: IThreeDCategoryListItem[] = [];

        matchedNodesByRoot.forEach((nodes, rootId) => {
          const root = initialCategoryList.find((r) => r.id === rootId)!;

          // 매칭된 모든 노드의 id를 Set으로 저장 (빠른 조회용)
          const matchedNodeIds = new Set(nodes.map((n) => n.node.id));

          // 각 매칭 노드의 전체 경로를 추적하여 어떤 자식이 매칭되었는지 파악
          // 모든 부모 노드에 대해 매칭된 자식 정보를 기록
          const matchedChildrenByParent = new Map<number, Set<number>>();

          for (const { node, parentPath } of nodes) {
            // 매칭 노드 자체도 매칭된 것으로 표시
            matchedNodeIds.add(node.id);

            // parentPath의 모든 부모에 대해 매칭된 자식을 표시
            // 직접 부모뿐만 아니라 상위 부모들도 포함
            for (let i = 0; i < parentPath.length; i++) {
              const parent = parentPath[i];
              if (!matchedChildrenByParent.has(parent.id)) {
                matchedChildrenByParent.set(parent.id, new Set());
              }
              // 각 부모의 직접 자식만 기록 (직접 자식이 매칭된 경우)
              if (i === parentPath.length - 1) {
                // 직접 부모의 경우, 매칭된 노드를 자식으로 추가
                matchedChildrenByParent.get(parent.id)!.add(node.id);
              } else {
                // 상위 부모의 경우, 다음 레벨의 자식을 자식으로 추가
                const nextChild = parentPath[i + 1];
                matchedChildrenByParent.get(parent.id)!.add(nextChild.id);
              }
            }
          }

          // 필터링된 트리 구조 생성
          const buildFilteredTree = (
            node: IThreeDCategoryListItem,
            isMatched: boolean
          ): IThreeDCategoryListItem | null => {
            // 현재 노드가 매칭되었거나, 매칭된 자식을 가지고 있는 경우에만 포함
            const hasMatchedChildren = matchedChildrenByParent.has(node.id);
            const nodeMatches = node.nameKo.includes(trimmedKeyword);

            if (!nodeMatches && !hasMatchedChildren) {
              return null;
            }

            // children 필터링
            const filteredChildren: IThreeDCategoryListItem[] = [];
            if (node.children && node.children.length > 0) {
              if (nodeMatches) {
                // 부모 노드가 매칭된 경우, 모든 자식을 그대로 포함 (재귀 필터링 없이)
                filteredChildren.push(...node.children);
              } else {
                // 부모 노드가 매칭되지 않은 경우, 매칭된 자식만 포함
                for (const child of node.children) {
                  const filteredChild = buildFilteredTree(child, matchedNodeIds.has(child.id));
                  if (filteredChild) {
                    filteredChildren.push(filteredChild);
                  }
                }
              }
            }

            // 루트 노드인 경우, 매칭된 자식이 있거나 노드 자체가 매칭된 경우에만 반환
            if (node.id === rootId) {
              if (filteredChildren.length > 0 || nodeMatches) {
                return {
                  ...node,
                  children: filteredChildren
                };
              }
              return null;
            }

            // 일반 노드: 매칭되었거나 매칭된 자식이 있는 경우
            return {
              ...node,
              children: filteredChildren
            };
          };

          const filteredTree = buildFilteredTree(root, matchedNodeIds.has(root.id));
          if (filteredTree) {
            filteredTrees.push(filteredTree);
          }
        });

        setSearchedCategoryList(filteredTrees);

        // 모든 매칭 노드의 부모 경로 id 수집
        const allParentIds = new Set<number>();
        const allNodeIdsToCollapse = new Set<number>();

        for (const { root, parentPath } of matchedNodes) {
          // targetRoot와 그 자식 노드들의 id 수집
          const collectNodeIds = (node: IThreeDCategoryListItem): number[] => {
            const ids = [node.id];
            if (node.children && node.children.length > 0) {
              node.children.forEach((child) => {
                ids.push(...collectNodeIds(child));
              });
            }
            return ids;
          };

          const nodeIdsToCollapse = collectNodeIds(root);
          nodeIdsToCollapse.forEach((id) => allNodeIdsToCollapse.add(id));

          // 부모 경로의 id들 수집
          parentPath.forEach((parent) => {
            allParentIds.add(parent.id);
          });
        }

        // expandedCategoryIds 업데이트
        setExpandedCategoryIds((prev) => {
          const newSet = new Set(prev);

          // targetRoot와 그 자식 노드들을 collapsed 상태로 변경
          allNodeIdsToCollapse.forEach((id) => {
            newSet.delete(id);
          });

          // 부모 카테고리들을 expanded 상태로 추가
          allParentIds.forEach((id) => {
            newSet.add(id);
          });

          return newSet;
        });
      } else {
        setSearchedCategoryList([]);
      }
    } else {
      setSearchedCategoryList([]);
      setSelectedCategoryId(null);
      setExpandedCategoryIds(new Set()); // expandedCategoryIds의 모든 값 제거
    }
  }, [searchKeyword, initialCategoryList]);

  return {
    inputKeyword,
    searchKeyword,
    autocompleteList,
    handleKeyDown,
    handleInputKeywordChange,
    handleAutocompleteChange,
    handleClear,
    handleSubmit,

    expandedCategoryIds,
    selectedCategoryId,
    handleCategoryExpand,
    handleCategoryClick,
    categoryListData: searchKeyword && searchKeyword.trim().length > 0 ? searchedCategoryList : initialCategoryList, // 검색 상태에 따라 다른 리스트 반환

    selectedCategoryPath,
    imageListData,
    isLoading,
    flattenImageList,
    outputBottomRef,

    selectedAssetThreeDId,
    setSelectedAssetThreeDId
  };
};
